/**
 * VariantManager — Sprint 56 redesign.
 * Each variant is a full, expandable Card with a clean responsive grid.
 * Backend endpoints unchanged:
 *   GET    /products/:pid/variants
 *   POST   /products/:pid/variants
 *   PATCH  /products/variants/:vid
 *   DELETE /products/variants/:vid
 */
import { useEffect, useState, useCallback } from "react";
import { api, errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Package, ChevronDown, ChevronUp,
  Copy, Star, ImagePlus,
} from "lucide-react";

const emptyVariant = {
  name: "",
  sku: "",
  barcode: "",
  unit: "piece",
  weight: "",
  weight_unit: "g",
  pack_size: "",
  image_url: "",
  mrp: "",
  selling_price: "",
  cost_price: "",
  initial_stock: "",
  track_stock: true,
  is_default: false,
  is_active: true,
  is_online: true, // Sprint — Online Visibility per variant (customer app / website)
};

// Larger 88x88 image tile used inside each variant card.
function VariantImageTile({ value, onChange, testId, size = 88 }) {
  const readAsDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ""));
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; }
    if (f.size / 1048576 > 3) { toast.error("Max 3 MB"); return; }
    try { onChange(await readAsDataURL(f)); }
    catch { toast.error("Could not read file"); }
    e.target.value = "";
  };
  return (
    <label
      className="rounded-xl border-2 border-dashed border-border/70 bg-secondary/30 grid place-items-center cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition overflow-hidden relative shrink-0"
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      {value ? (
        <>
          <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-black/45 text-white text-[10px] text-center py-0.5 opacity-0 group-hover:opacity-100">
            Change
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          <ImagePlus className="w-6 h-6" />
          <span className="text-[10px] font-medium">Image</span>
        </div>
      )}
      <input type="file" accept="image/*" className="hidden" onChange={pick} />
    </label>
  );
}

// ─── SINGLE VARIANT CARD ────────────────────────────────────────────────
function VariantCard({ row, productId, onChange, onReload, isNew, onCancelNew }) {
  // Local editing state so users can tweak all fields freely before saving.
  const [local, setLocal] = useState(row);
  const [expanded, setExpanded] = useState(isNew ? true : !!row._expanded);
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setLocal((prev) => ({ ...prev, [k]: v }));

  useEffect(() => { setLocal(row); }, [row.id]); // reset when reloaded

  const dirty = JSON.stringify({ ...local, stock_delta: undefined, current_stock: undefined }) !==
                JSON.stringify({ ...row,   stock_delta: undefined, current_stock: undefined });

  const castPayload = () => ({
    name: (local.name || "").trim(),
    sku: (local.sku || "").trim() || null,
    barcode: local.barcode || null,
    weight: local.weight === "" || local.weight == null ? null : Number(local.weight),
    weight_unit: local.weight_unit || "g",
    pack_size: local.pack_size || null,
    image_url: local.image_url || null,
    mrp: Number(local.mrp || 0),
    selling_price: Number(local.selling_price || 0),
    cost_price: local.cost_price === "" || local.cost_price == null ? null : Number(local.cost_price),
    unit: local.unit || "piece",
    is_active: !!local.is_active,
    is_default: !!local.is_default,
    is_online: local.is_online !== false, // Sprint — Online Visibility per variant
  });

  const createNew = async () => {
    if (!local.name || !local.selling_price) {
      toast.error("Name and Selling price are required");
      return;
    }
    setBusy(true);
    try {
      const initialStock = Number(local.initial_stock || 0);
      const { data: newVariant } = await api.post(`/products/${productId}/variants`, castPayload());
      if (initialStock > 0 && newVariant?.id) {
        try {
          await api.post("/inventory/adjust", {
            product_id: productId,
            variant_id: newVariant.id,
            delta_qty: initialStock,
            reason: "initial_stock",
          });
        } catch (e) {
          toast.warning("Variant saved, stock adjust failed: " + (errMsg(e) || "unknown"));
        }
      }
      toast.success("Variant added");
      onCancelNew?.();
      await onReload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const saveExisting = async () => {
    setBusy(true);
    try {
      await api.patch(`/products/variants/${row.id}`, castPayload());
      const delta = Number(local.stock_delta || 0);
      if (delta !== 0) {
        try {
          await api.post("/inventory/adjust", {
            product_id: productId,
            variant_id: row.id,
            delta_qty: delta,
            reason: "manual_edit",
          });
          toast.success(`Variant updated · stock ${delta > 0 ? "+" : ""}${delta}`);
        } catch (e) {
          toast.warning("Variant saved, stock adjust failed: " + (errMsg(e) || "unknown"));
        }
      } else {
        toast.success("Variant updated");
      }
      await onReload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this variant?")) return;
    setBusy(true);
    try {
      await api.delete(`/products/variants/${row.id}`);
      toast.success("Variant removed");
      await onReload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const duplicate = () => {
    onChange?.({
      ...emptyVariant,
      ...row,
      id: undefined,
      _isNew: true,
      name: `${row.name || ""} (copy)`,
      sku: "",
      is_default: false,
      initial_stock: "",
    });
  };

  const price = Number(local.selling_price || 0);
  const mrp = Number(local.mrp || 0);
  const discount = mrp > 0 && price > 0 && price < mrp ? Math.round(((mrp - price) / mrp) * 100) : 0;

  return (
    <div
      className={`group rounded-xl border bg-card shadow-sm hover:shadow transition ${
        isNew ? "border-primary/60 ring-2 ring-primary/15" : "border-border"
      }`}
      data-testid={isNew ? "variant-card-new" : `variant-card-${row.id}`}
    >
      {/* HEADER — always visible */}
      <div className="flex items-center gap-3 p-3">
        <VariantImageTile
          value={local.image_url || ""}
          onChange={(v) => setField("image_url", v)}
          testId={isNew ? "variant-new-image" : `variant-image-${row.id}`}
          size={56}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">
              {local.name || <span className="text-muted-foreground italic font-normal">Untitled variant</span>}
            </span>
            {local.is_default && (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200 text-[10px] py-0 px-1.5 h-5 gap-1">
                <Star className="w-3 h-3 fill-amber-500" /> Default
              </Badge>
            )}
            {!local.is_active && (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-5">Inactive</Badge>
            )}
            {discount > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 text-[10px] py-0 px-1.5 h-5">
                {discount}% off
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {local.sku && (
              <span className="font-mono bg-muted/60 px-1.5 py-0.5 rounded text-[11px] text-foreground font-medium">
                {local.sku}
              </span>
            )}
            {price > 0 && (
              <div className="flex items-center gap-1.5">
                {local.sku && <span className="text-muted-foreground/40">·</span>}
                <span className="font-semibold text-foreground text-sm">{money(price)}</span>
                {mrp > 0 && mrp > price && <span className="line-through text-muted-foreground text-xs">{money(mrp)}</span>}
              </div>
            )}
            {!isNew && (
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground/40">·</span>
                <span>Stock: <strong className="text-foreground">{row.current_stock ?? 0}</strong></span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {!isNew && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={duplicate}
              disabled={busy}
              title="Duplicate variant"
              data-testid={`variant-duplicate-${row.id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
          )}
          {!isNew && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
              onClick={del}
              disabled={busy}
              title="Delete variant"
              data-testid={`variant-delete-${row.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Collapse" : "Expand"}
            data-testid={isNew ? "variant-new-toggle" : `variant-toggle-${row.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* BODY — expandable grid */}
      {expanded && (
        <div className="border-t px-3 md:px-4 py-3.5 space-y-3.5 bg-muted/5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Pack name" required>
              <Input
                value={local.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. 1 Kg"
                className="h-9"
                data-testid={isNew ? "variant-new-name" : `variant-name-${row.id}`}
              />
            </Field>
            <Field label="SKU" hint="Optional · auto-generated">
              <Input
                value={local.sku || ""}
                onChange={(e) => setField("sku", e.target.value)}
                placeholder="Auto"
                className="h-9 font-mono text-sm"
                data-testid={isNew ? "variant-new-sku" : `variant-sku-${row.id}`}
              />
            </Field>
            <Field label="Barcode" hint="EAN / UPC">
              <Input
                value={local.barcode || ""}
                onChange={(e) => setField("barcode", e.target.value)}
                placeholder="—"
                className="h-9 font-mono text-sm"
                data-testid={isNew ? "variant-new-barcode" : `variant-barcode-${row.id}`}
              />
            </Field>

            <Field label="MRP ₹">
              <Input
                type="number"
                value={local.mrp ?? ""}
                onChange={(e) => setField("mrp", e.target.value)}
                placeholder="0"
                className="h-9"
                data-testid={isNew ? "variant-new-mrp" : `variant-mrp-${row.id}`}
              />
            </Field>
            <Field label="Selling price ₹" required>
              <Input
                type="number"
                value={local.selling_price ?? ""}
                onChange={(e) => setField("selling_price", e.target.value)}
                placeholder="0"
                className="h-9"
                data-testid={isNew ? "variant-new-price" : `variant-price-${row.id}`}
              />
            </Field>
            <Field label="Cost price ₹" hint="Optional">
              <Input
                type="number"
                value={local.cost_price ?? ""}
                onChange={(e) => setField("cost_price", e.target.value)}
                placeholder="0"
                className="h-9"
              />
            </Field>

            <Field label="Weight">
              <Input
                type="number"
                value={local.weight ?? ""}
                onChange={(e) => setField("weight", e.target.value)}
                placeholder="e.g. 500"
                className="h-9"
              />
            </Field>
            <Field label="Unit">
              <select
                value={local.weight_unit || "g"}
                onChange={(e) => setField("weight_unit", e.target.value)}
                className="w-full h-9 border border-input rounded-md px-2.5 bg-background text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {["g", "kg", "ml", "L", "pc", "pcs"].map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>

            {isNew ? (
              <Field label="Initial stock">
                <Input
                  type="number"
                  min="0"
                  value={local.initial_stock ?? ""}
                  onChange={(e) => setField("initial_stock", e.target.value)}
                  placeholder="e.g. 50"
                  className="h-9"
                  data-testid="variant-new-stock"
                />
              </Field>
            ) : (
              <Field label={`Stock adjust · currently ${row.current_stock ?? 0}`}>
                <Input
                  type="number"
                  value={local.stock_delta || ""}
                  onChange={(e) => setField("stock_delta", e.target.value)}
                  placeholder="+50 or -3"
                  className="h-9"
                  data-testid={`variant-stock-delta-${row.id}`}
                />
              </Field>
            )}
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <Switch
                checked={!!local.is_default}
                onCheckedChange={(v) => setField("is_default", v)}
                data-testid={isNew ? "variant-new-default" : `variant-default-${row.id}`}
              />
              <span>Default variant</span>
            </label>
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <Switch
                checked={!!local.is_active}
                onCheckedChange={(v) => setField("is_active", v)}
                data-testid={isNew ? "variant-new-active" : `variant-active-${row.id}`}
              />
              <span>Active</span>
            </label>
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer" title="OFF = POS only. ON = Website/App + POS.">
              <Switch
                checked={local.is_online !== false}
                onCheckedChange={(v) => setField("is_online", v)}
                data-testid={isNew ? "variant-new-online" : `variant-online-${row.id}`}
              />
              <span>Show Online</span>
            </label>
          </div>

          {/* Action row */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-3 border-t">
            <div className="text-[11px] text-muted-foreground text-center sm:text-left">
              {isNew
                ? "Fill Pack name and Selling price to create this variant."
                : dirty
                  ? <span className="text-amber-600 font-medium">Unsaved changes</span>
                  : "All changes saved."}
            </div>
            {isNew ? (
              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCancelNew}
                  disabled={busy}
                  className="h-8 text-xs font-medium"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={createNew}
                  disabled={busy || !local.name || !local.selling_price}
                  className="h-8 gap-1.5 text-xs font-medium shadow-sm"
                  data-testid="variant-new-add"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {busy ? "Adding…" : "Add variant"}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-end">
                <Button
                  size="sm"
                  onClick={saveExisting}
                  disabled={busy || !dirty}
                  variant={dirty ? "default" : "secondary"}
                  className="h-8 gap-1.5 text-xs font-medium shadow-sm"
                  data-testid={`variant-save-${row.id}`}
                >
                  <Save className="w-3.5 h-3.5" />
                  {busy ? "Saving…" : "Save changes"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Small labeled field wrapper
function Field({ label, hint, required, children }) {
  return (
    <div className="min-w-0">
      <Label className="text-[11px] font-semibold text-muted-foreground flex items-center justify-between gap-1 mb-1">
        <span className="uppercase tracking-wide">
          {label}{required && <span className="text-primary ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[10px] font-normal text-muted-foreground/70 normal-case">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────
export default function VariantManager({ productId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newDraft, setNewDraft] = useState(null); // null = closed, object = a card is open

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [vRes, iRes] = await Promise.all([
        api.get(`/products/${productId}/variants`),
        api.get(`/inventory?product_id=${productId}&limit=100`).catch(() => ({ data: { items: [] } })),
      ]);
      const invByVar = new Map();
      for (const inv of (iRes.data.items || [])) {
        if (inv.variant_id) invByVar.set(String(inv.variant_id), Number(inv.qty_on_hand || 0));
      }
      const items = (vRes.data.items || []).map((r) => ({
        ...r,
        current_stock: invByVar.get(String(r.id)) ?? 0,
        stock_delta: "",
      }));
      setRows(items);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const openNew = (prefill = null) => {
    setNewDraft(prefill ? { ...prefill } : { ...emptyVariant });
    // Scroll to the new card after mount
    setTimeout(() => {
      document.querySelector('[data-testid="variant-card-new"]')?.scrollIntoView({
        behavior: "smooth", block: "center",
      });
    }, 50);
  };

  return (
    <div className="space-y-3" data-testid="variant-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {rows.length > 0 && (
            <Badge variant="secondary" className="text-[10px] rounded-full">
              {rows.length} pack{rows.length > 1 ? "s" : ""}
            </Badge>
          )}
          <span className="hidden sm:inline">Different pack sizes with their own SKU, price and stock (e.g. 500g, 1kg, 5kg).</span>
        </div>
        <Button
          size="sm"
          onClick={() => openNew()}
          disabled={!!newDraft}
          className="h-8 gap-1.5 text-xs font-medium shadow-sm ml-auto"
          data-testid="variant-add-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Add variant
        </Button>
      </div>

      {/* Existing variants */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-secondary/40 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 && !newDraft ? (
        <div className="rounded-xl border-2 border-dashed border-border/70 bg-card px-4 py-8 text-center">
          <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center mx-auto mb-2">
            <Package className="w-5 h-5 text-primary" />
          </div>
          <div className="text-sm font-semibold">No variants yet</div>
          <div className="text-xs text-muted-foreground mt-0.5 max-w-sm mx-auto">
            Add different pack sizes (e.g. 500g, 1kg, 5kg) — each with its own SKU, price and stock.
          </div>
          <Button
            size="sm"
            onClick={() => openNew()}
            className="mt-3 h-8 gap-1.5 text-xs font-medium shadow-sm"
            data-testid="variant-add-empty-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add your first variant
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 w-full" data-testid="variant-list">
          {rows.map((r) => (
            <VariantCard
              key={r.id}
              row={r}
              productId={productId}
              onReload={load}
              onChange={(patch) => {
                if (patch?._isNew) {
                  openNew(patch);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Add-variant expanded card */}
      {newDraft && (
        <div className="mt-3">
          <VariantCard
            row={{ ...newDraft, _expanded: true }}
            productId={productId}
            isNew
            onReload={async () => { setNewDraft(null); await load(); }}
            onCancelNew={() => setNewDraft(null)}
          />
        </div>
      )}
    </div>
  );
}
